import React, { useState, useEffect, useRef } from 'react';
import {
  GlassButton,
  GlassSelect,
  GlassInput,
} from '@/design-system';
import type { CostProfile, TeamCostAllocation } from '@/types';
import { formatCurrencyFull } from '@/utils/formatters';

interface CostProfileBuilderProps {
  costProfiles: CostProfile[];
  totalMembers: number;
  initialAllocations?: TeamCostAllocation[];
  onChange: (allocations: Array<{ costProfileId: string; resourceCount: number }>) => void;
  error?: string;
}

const CostProfileBuilder: React.FC<CostProfileBuilderProps> = ({
  costProfiles,
  totalMembers,
  initialAllocations = [],
  onChange,
  error,
}) => {
  const [allocations, setAllocations] = useState<Array<{ id: string; costProfileId: string; resourceCount: number }>>([]);
  const initializedRef = useRef(false);
  const prevInitialSignature = useRef<string | null>(null);

  useEffect(() => {
    const signature = JSON.stringify(initialAllocations);
    const initialChanged = signature !== prevInitialSignature.current;
    prevInitialSignature.current = signature;

    // If parent provided allocations, sync once per change
    if (initialAllocations.length > 0 && initialChanged) {
      const mapped = initialAllocations.map((alloc, index) => ({
        id: `alloc-${index}`,
        costProfileId: alloc.costProfileId,
        resourceCount: alloc.resourceCount,
      }));
      initializedRef.current = true;
      setAllocations(mapped);
      return;
    }

    // Initialize default only once when nothing provided
    if (!initializedRef.current && costProfiles.length > 0) {
      const firstProfile = costProfiles[0];
      if (firstProfile) {
        initializedRef.current = true;
        setAllocations([{
          id: 'alloc-0',
          costProfileId: firstProfile.id,
          resourceCount: totalMembers,
        }]);
      }
    }
  }, [initialAllocations, costProfiles, totalMembers]);

  useEffect(() => {
    // Notify parent of changes
    const cleaned = allocations.map(({ costProfileId, resourceCount }) => ({
      costProfileId,
      resourceCount,
    }));
    onChange(cleaned);
  }, [allocations, onChange]);

  const addAllocation = () => {
    const remainingMembers = totalMembers - getAllocatedCount();
    if (remainingMembers > 0 && costProfiles.length > 0) {
      const usedProfiles = new Set(allocations.map(a => a.costProfileId));
      const availableProfile = costProfiles.find(cp => !usedProfiles.has(cp.id));

      if (availableProfile) {
        setAllocations([
          ...allocations,
          {
            id: `alloc-${Date.now()}`,
            costProfileId: availableProfile.id,
            resourceCount: Math.min(remainingMembers, 1),
          },
        ]);
      }
    }
  };

  const removeAllocation = (id: string) => {
    if (allocations.length > 1) {
      setAllocations(allocations.filter(a => a.id !== id));
    }
  };

  const updateAllocation = (id: string, field: 'costProfileId' | 'resourceCount', value: string | number) => {
    setAllocations(allocations.map(alloc =>
      alloc.id === id
        ? { ...alloc, [field]: field === 'resourceCount' ? Number(value) : value }
        : alloc
    ));
  };

  const getAllocatedCount = () => {
    return allocations.reduce((sum, alloc) => sum + alloc.resourceCount, 0);
  };

  const getRemainingCount = () => {
    return totalMembers - getAllocatedCount();
  };

  const getBlendedRate = () => {
    if (allocations.length === 0) return 0;

    const totalCost = allocations.reduce((sum, alloc) => {
      const profile = costProfiles.find(cp => cp.id === alloc.costProfileId);
      return sum + (profile ? Number(profile.hourlyRate) * alloc.resourceCount : 0);
    }, 0);

    const totalResources = getAllocatedCount();
    return totalResources > 0 ? totalCost / totalResources : 0;
  };

  const isValid = getAllocatedCount() === totalMembers;

  const getAvailableProfiles = (currentId: string) => {
    const usedProfiles = new Set(
      allocations
        .filter(a => a.costProfileId !== currentId)
        .map(a => a.costProfileId)
    );
    return costProfiles.filter(cp => !usedProfiles.has(cp.id) || cp.id === currentId);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h4 className="text-sm font-medium text-[var(--text)]">Cost Profile Distribution</h4>
          <p className="text-xs text-[var(--muted)] mt-1">
            Allocate {totalMembers} team members across cost profiles
          </p>
        </div>
        <GlassButton
          type="button"
          variant="ghost"
          size="sm"
          onClick={addAllocation}
          disabled={getRemainingCount() <= 0 || allocations.length >= costProfiles.length}
        >
          + Add Profile
        </GlassButton>
      </div>

      {/* Allocations */}
      <div className="space-y-3">
        {allocations.map((alloc) => {
          const availableProfiles = getAvailableProfiles(alloc.costProfileId);
          const profileOptions = availableProfiles.map(cp => ({
            value: cp.id,
            label: `${cp.name} ($${cp.hourlyRate}/hour)`,
          }));

          return (
            <div key={alloc.id} className="flex items-center gap-3 p-3 rounded-xl border border-[var(--glass-border-subtle)]">
              <div className="flex-1">
                <GlassSelect
                  value={alloc.costProfileId}
                  onChange={(value) => updateAllocation(alloc.id, 'costProfileId', value)}
                  options={profileOptions}
                  size="sm"
                  fullWidth
                />
              </div>

              <div className="w-32">
                <GlassInput
                  type="number"
                  value={alloc.resourceCount.toString()}
                  onChange={(value) => updateAllocation(alloc.id, 'resourceCount', value)}
                  min={1}
                  max={totalMembers}
                  size="sm"
                  fullWidth
                />
              </div>

              <div className="w-24 text-xs text-[var(--muted)]">
                {alloc.resourceCount} member{alloc.resourceCount !== 1 ? 's' : ''}
              </div>

              {allocations.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeAllocation(alloc.id)}
                  className="text-status-error hover:text-status-error/80 p-2"
                  title="Remove"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Summary */}
      <div className="rounded-xl border border-[var(--glass-border-subtle)] p-4 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-[var(--muted)]">Total Allocated:</span>
          <span className={`font-medium ${isValid ? 'text-status-success' : 'text-status-warning'}`}>
            {getAllocatedCount()} / {totalMembers}
          </span>
        </div>

        <div className="flex justify-between text-sm">
          <span className="text-[var(--muted)]">Remaining:</span>
          <span className={`font-medium ${getRemainingCount() === 0 ? 'text-status-success' : 'text-status-warning'}`}>
            {getRemainingCount()}
          </span>
        </div>

        <div className="flex justify-between text-sm border-t border-[var(--glass-border-subtle)] pt-2">
          <span className="text-[var(--muted)]">Blended Rate:</span>
          <span className="font-medium text-[var(--text)]">
            {formatCurrencyFull(getBlendedRate(), { fallback: '$0' })}/hour
          </span>
        </div>

        <div className="flex justify-between text-sm">
          <span className="text-[var(--muted)]">Weekly Cost:</span>
          <span className="font-medium text-[var(--text)]">
            {formatCurrencyFull(getBlendedRate() * getAllocatedCount() * 40)}
          </span>
        </div>
      </div>

      {!isValid && (
        <p className="text-sm text-status-warning">
          Please allocate all {totalMembers} team members
        </p>
      )}

      {error && <p className="text-xs text-status-error font-medium">{error}</p>}
    </div>
  );
};

export default CostProfileBuilder;
